import { getFieldAttributes, getFieldItems } from "../../../helpers/formDataHelpers";
import appSettings from "../../../settings/appSettings";
import { useFormFlowContext } from "../FormFlowContext";
import MultiselectCheckboxes from "./MultiselectCheckboxes";
import RadioGroup from "./RadioGroup";
import Select from "./Select";
import TextInput from "./TextInput";

/**
 * DynamicFormField component renders different types of form fields based on the provided type.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.wrapperClass - The CSS class for the wrapper div.
 * @param {Object} props.formMethods - The methods for handling form state and validation.
 * @param {Array} props.formFields - The array of wvus-form fields.
 * @param {string} props.type - The type of form field to render (e.g., "multi-select-checkboxes", "text-input", "select", "radio").
 * @param {string} props.validationType - The type of validation to apply to the form field.
 * @param {Object} props.field - The field object containing field-specific data from config.
 * @param {Object} props.user - The user object containing user-specific data.
 * @param {number} props.columns - The number of columns for grid layout (optional).
 *
 * @returns {JSX.Element} The rendered form field component.
 */
const DynamicFormField = ({
  wrapperClass,
  formMethods,
  formFields,
  type,
  validationType,
  field,
  columns: numberOfColumns
}) => {
  const { prepopulateComplete, userData } = useFormFlowContext();
  const { supporterData } = appSettings;
  const userInformation = userData || supporterData; // fallback if userData was not populated

  const fieldAttributes = getFieldAttributes(
    field,
    userInformation,
    formFields,
    prepopulateComplete
  );
  const items = getFieldItems(field);

  switch (type) {
    case "multi-select-checkboxes":
      return (
        <div className={wrapperClass}>
          <MultiselectCheckboxes
            formMethods={formMethods}
            items={items}
            wrapperClass={numberOfColumns ? `grid-columns-${numberOfColumns}` : ""}
          />
        </div>
      );
    case "text-input":
      return (
        <div className={wrapperClass}>
          <TextInput
            formMethods={formMethods}
            fieldAttributes={fieldAttributes}
            validationType={validationType}
          />
        </div>
      );
    case "select":
      return (
        <div className={wrapperClass}>
          <Select formMethods={formMethods} items={items} fieldAttributes={fieldAttributes} />
        </div>
      );
    case "radio":
      return (
        <div className={wrapperClass}>
          <RadioGroup formMethods={formMethods} items={items} questionName={field.name} />
        </div>
      );
    default:
      return null;
  }
};

export default DynamicFormField;
